import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { api, type AssistantTurn, type MeResponse } from '../api';
import { messageFor } from '../App';
import { AssistantBar } from './AssistantBar';

export interface ActivityLogHandle {
  reload: () => void;
}

/**
 * Anything the assistant is still waiting on, and a foldable record of what it has done.
 *
 * Open threads are the only part that needs attention, so they are the only part expanded by
 * default. Everything finished collapses into one line: it is worth keeping — it is how a
 * misheard word gets caught — but it is history, and history should not push the calendar
 * off the screen.
 */
export const ActivityLog = forwardRef<
  ActivityLogHandle,
  { me: MeResponse; limit?: number; onChanged?: () => void; inline?: boolean }
>(function ActivityLog({ me, limit = 12, onChanged, inline = false }, ref) {
  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const [error, setError] = useState<string>();
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    try {
      setTurns((await api.assistantHistory(limit)).turns);
      setError(undefined);
    } catch (err) {
      setError(messageFor(err));
    }
  }, [limit]);

  useEffect(() => {
    void load();
  }, [load]);

  useImperativeHandle(ref, () => ({ reload: () => void load() }), [load]);

  async function dismiss(conversationId: string) {
    try {
      await api.closeConversation(conversationId);
      await load();
    } catch (err) {
      setError(messageFor(err));
    }
  }

  if (error) return <div className="notice error">{error}</div>;
  if (turns.length === 0) return null;

  const open = turns.filter((turn) => turn.status === 'open');
  const done = turns.filter((turn) => turn.status !== 'open');

  // Rendered inside the prompt's panel rather than as one of its own, so a question the
  // assistant is waiting on sits directly under the box you would answer it in.
  return (
    <div className={inline ? 'activity inline' : 'panel activity'}>
      {open.map((turn) => (
        <div key={turn.turnId} className="turn awaiting">
          <div className="turn-head">
            <span className="pill warn">waiting on you</span>
            <span className="spacer" />
            <span className="muted">{time(turn.at)}</span>
          </div>

          {turn.heard !== null ? (
            <p className="turn-heard">Heard: “{turn.heard}”</p>
          ) : (
            <p className="turn-heard">You: {turn.said}</p>
          )}
          <p className="turn-reply">{turn.reply}</p>

          <div className="turn-reply-box">
            <AssistantBar
              me={me}
              compact
              autoFocus
              conversationId={turn.conversationId}
              placeholder="Answer, by typing or speaking…"
              sendLabel="Reply"
              onDone={() => {
                void load();
                onChanged?.();
              }}
            />
            <button className="link" onClick={() => void dismiss(turn.conversationId)}>
              Never mind — mark this done
            </button>
          </div>
        </div>
      ))}

      {done.length > 0 && (
        <>
          <button className="disclosure" onClick={() => setShowDone((v) => !v)}>
            {showDone ? '▾' : '▸'} {done.length} completed
          </button>

          {showDone &&
            done.map((turn) => {
              const failed = turn.toolCalls.filter((call) => !call.ok);
              return (
                <div key={turn.turnId} className="turn done">
                  <div className="turn-head">
                    <span className="muted">{time(turn.at)}</span>
                    <span className="spacer" />
                    {turn.mutations.length > 0 ? (
                      <span className="pill done-ok">
                        ✓ {turn.mutations.length} recorded
                      </span>
                    ) : (
                      <span className="pill">no change</span>
                    )}
                  </div>
                  <p className="turn-heard">
                    {turn.heard !== null ? `Heard: “${turn.heard}”` : `You: ${turn.said}`}
                  </p>
                  <p className="turn-reply">{turn.reply}</p>
                  {failed.length > 0 && (
                    <p className="turn-error">
                      {failed.map((call) => `${call.name}: ${call.error}`).join(' · ')}
                    </p>
                  )}
                </div>
              );
            })}
        </>
      )}
    </div>
  );
});

function time(at: string): string {
  return new Date(at).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
